// uploadProposal.jsx
import FileUpload from "components/FileUpload";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState, useRef, useEffect } from "react";
import { updateLegislativeDrafting } from "services/legislativeService";
import { createSectorDirectiveAdditionalInfo, updateSectorDirective, updateSectorDirectiveAdditionalInfoRequest } from "services/sectorDirectives";
import { LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { useTranslation } from "react-i18next";

interface uploadProposalProps {
    request: any;
    isOpen: boolean;
    onClose: () => void;
    setLegalResearch: Function;
    onUploadProposal: (data: {
        proposalSummary: string;
        proposalFile: string;
    }) => void;
}

const RequestForAdditionalInfo: React.FC<uploadProposalProps> = ({
    request,
    isOpen,
    setLegalResearch,
    onUploadProposal,
    onClose,
}) => {
    const { t } = useTranslation();
    const [proposalSummary, setProposalSummary] = useState(request.proposal_summary || "");
    const [proposalFile, setProposalFile] = useState();
    const contextData = useAuth();
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);

    // Create a ref for the modal content div
    const modalContentRef = useRef<HTMLDivElement>(null);

    // Effect to handle clicks outside the modal
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // If the modal is open and the click is outside the modal content
            if (
                modalContentRef.current &&
                !modalContentRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        // Cleanup the event listener when the modal closes or component unmounts
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setLegalResearch((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setValidationError(null);

        setSubmitting(true);

        try {
            const data = new FormData();
            if (proposalFile)
                data.append("additional_info_request_files", proposalFile ?? null);

            if (request.additional_infos_accurecy?.[0]) {
                data.append("request_summary", proposalSummary);
                const additionalInfoResponse = await updateSectorDirectiveAdditionalInfoRequest(request.additional_infos_accurecy?.[0].id, data, contextData.token || contextData?.user?.access_token || "");
            }
            else {
                data.append("directive_request", request.id);
                data.append("requested_by", contextData.user?.roles[0]);
                data.append("requested_by_id", contextData.user?.id ?? "");
                data.append("request_summary", proposalSummary);
                const additionalInfoResponse = await createSectorDirectiveAdditionalInfo(data, contextData.token || contextData?.user?.access_token || "");
            }
            const draftData = new FormData();
            draftData.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.waiting_for_additional_info.value);
            const response = await updateSectorDirective(request.id, draftData, contextData.token || contextData?.user?.access_token || "");
            setLegalResearch((prev: any) => ({
                ...prev,
                ...response,
            }));

            showToast(t("AdditionalInformationRequested") || "Additional Information Requested", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToRequestAdditionalInformation") || "Failed to Request Additional Information.", "error");
        } finally {
            setSubmitting(false);
        }

        onClose();
    };

    const handleFileChange = (
        file: any,
    ) => {
        setProposalFile(file);
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end p-4 z-50 overflow-hidden">
            {/* Modal content area - attach the ref here */}
            <div
                ref={modalContentRef}
                className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative flex flex-col h-full my-auto overflow-y-auto pr-2 custom-scrollbar"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {t("RequestForAdditionalInfo") || "Request For Additional Information"}
                </h2>

                <form onSubmit={handleSubmit} className="flex flex-col">
                    {/* Scrollable Content Area */}
                    <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
                        {/* Remark Section */}
                        <div className="mb-4">
                            <label
                                htmlFor="remark"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("RequestDetail") || "Request Detail"}
                            </label>
                            <textarea
                                id="remark"
                                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                                placeholder={t("DetailHere") || "detail here..."}
                                value={proposalSummary}
                                onChange={(e) => setProposalSummary(e.target.value)}
                            ></textarea>
                        </div>

                        {/* Proposal Deadline Schedule */}
                        <div className="mb-6">
                            <label
                                htmlFor="deadline"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("RequestedDocumentDetail") || "Requested Document Detail"}
                            </label>
                            <div className="relative">
                                <FileUpload onChange={handleFileChange} />
                            </div>
                        </div>

                    </div>
                    {validationError && (
                        <p className="text-red-600 text-sm mb-4">{validationError}</p>
                    )}
                    <div className="flex space-x-4 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-gray-800 hover:bg-[#13294d] text-white font-bold py-3 px-6 rounded-md disabled:opacity-70"
                        >
                            {submitting ? (
                                <div className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                                    </svg>
                                    {t("Submitting") || "Submitting..."}
                                </div>
                            ) : t("Submit") || "Submit"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RequestForAdditionalInfo;